import { useAppDispatch, useAppSelector } from "@/app/reduxHooks";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SchemaDefinitionForm, {
    SchemaDefinitionField,
} from "@/features/adp/components/shared/SchemaDefinitionForm";
import { closeOverlayAsync } from "@/features/adp/state/overlay.thunks";
import { openOverlay, popOverlay, SourceConfigurationOverlayData } from "@/features/adp/state/overlaySlice";
import { useTestConnectionMutation } from "@/features/adp/mutations/useConnectionMutations";
import { useAdpSessionStore } from "@/features/adp/hooks/useAdpSessionStore";
import {
    PersistedSourceConfigurationState,
    useFinalizeSourceConfigurationMutation,
} from "@/features/adp/hooks/useFinalizeSourceConfigurationMutation";
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from "@/ui/sheet";
import PickerDetailsHeader from "@/features/adp/components/shared/PickerDetailsHeader";
import { Button } from "@/ui/button";
import { ChevronLeft } from "lucide-react";
import { DELIMITERS } from "@/features/adp/constants/datasource.constants";
import styles from "./SourceSheets.module.scss";

type SourceNodeScopedState = {
    postTestFields: SchemaDefinitionField[];
    isConnectionTested: boolean;
    isBaseFormValid: boolean;
    isCombinedFormValid: boolean;
};

const EMPTY_SOURCE_SCHEMA: SchemaDefinitionField[] = [];

const EXCEL_ACCEPTABLE_FILE_EXTENSIONS = ['.csv', '.tsv', '.txt', '.xlsx', '.xls'];
const EXCEL_ACCEPT_STRING = EXCEL_ACCEPTABLE_FILE_EXTENSIONS.join(',');
const DELIMITER_NAME_TO_VALUE = new Map(DELIMITERS.map((delimiter) => [delimiter.name, delimiter.value]));

const SourceConfiguration = () => {
    const { isOpen, type, data } = useAppSelector(state => state.overlay);
    const isSourceConfigurationOpen = isOpen && type === 'source-configuration';
    const sourceConfigurationData = isSourceConfigurationOpen ? data as SourceConfigurationOverlayData : null;
    const dispatch = useAppDispatch();
    const { getNode } = useAdpSessionStore();

    const formContainerRef = useRef<HTMLDivElement | null>(null);
    const isNavigatingBackRef = useRef(false);
    /** Form values updated on every keystroke — kept in a ref so the sheet does not re-render per key. */
    const latestValuesByNodeRef = useRef<Record<string, Record<string, string>>>({});

    const nodeKey = sourceConfigurationData?.nodeId ?? "__source_configuration_closed__";
    const nodeKeyRef = useRef(nodeKey);
    nodeKeyRef.current = nodeKey;

    const sourceConfigurationDataRef = useRef(sourceConfigurationData);
    sourceConfigurationDataRef.current = sourceConfigurationData;

    const persistedNodeState = useMemo<PersistedSourceConfigurationState | null>(() => {
        const id = sourceConfigurationData?.nodeId;
        if (!id) return null;
        const node = getNode(id) as { sourceConfigurationState?: PersistedSourceConfigurationState } | null;
        return node?.sourceConfigurationState ?? null;
    }, [sourceConfigurationData?.nodeId, getNode]);

    const persistedNodeStateRef = useRef(persistedNodeState);
    persistedNodeStateRef.current = persistedNodeState;

    const buildEmptyNodeStateForPersisted = useCallback(
        (persisted: PersistedSourceConfigurationState | null): SourceNodeScopedState => ({
            postTestFields: persisted?.postTestFields ?? EMPTY_SOURCE_SCHEMA,
            isConnectionTested: persisted?.isConfigured ?? false,
            isBaseFormValid: false,
            isCombinedFormValid: false,
        }),
        [],
    );

    const [nodeScopedState, setNodeScopedState] = useState<Record<string, SourceNodeScopedState>>({});
    const selectedFileRef = useRef<File | null>(null);

    const isExcelSource = useMemo(() => {
        return (
            (sourceConfigurationData?.data_source_type ?? '').toUpperCase() === 'FILE' &&
            (sourceConfigurationData?.data_source_instance ?? '').toLowerCase() === 'excel'
        );
    }, [sourceConfigurationData?.data_source_type, sourceConfigurationData?.data_source_instance]);

    const isExcelSourceRef = useRef(isExcelSource);
    isExcelSourceRef.current = isExcelSource;

    useEffect(() => {
        selectedFileRef.current = null;
    }, [nodeKey]);

    const currentNodeState = nodeScopedState[nodeKey] ?? buildEmptyNodeStateForPersisted(persistedNodeState);

    const postTestFields = currentNodeState.postTestFields;
    const isConnectionTested = currentNodeState.isConnectionTested;
    const isBaseFormValid = currentNodeState.isBaseFormValid;
    const isCombinedFormValid = currentNodeState.isCombinedFormValid;

    const handleValidityChange = useCallback((isValid: boolean) => {
        setNodeScopedState((prevState) => {
            const key = nodeKeyRef.current;
            const previousNodeState = prevState[key] ?? buildEmptyNodeStateForPersisted(persistedNodeStateRef.current);
            const nextBaseFormValid = previousNodeState.postTestFields.length > 0
                ? previousNodeState.isBaseFormValid
                : isValid;

            if (
                previousNodeState.isCombinedFormValid === isValid
                && previousNodeState.isBaseFormValid === nextBaseFormValid
            ) {
                return prevState;
            }

            return {
                ...prevState,
                [key]: {
                    ...previousNodeState,
                    isCombinedFormValid: isValid,
                    isBaseFormValid: nextBaseFormValid,
                },
            };
        });
    }, [buildEmptyNodeStateForPersisted]);

    const handleOpenChange = useCallback((open: boolean) => {
        if (!open) {
            if (isNavigatingBackRef.current) {
                isNavigatingBackRef.current = false;
                return;
            }
            dispatch(closeOverlayAsync());
        }
    }, [dispatch]);

    const handleCancel = useCallback(() => handleOpenChange(false), [handleOpenChange]);

    const handleBack = useCallback(() => {
        isNavigatingBackRef.current = true;
        dispatch(popOverlay());
    }, [dispatch, popOverlay]);

    const handleValuesChange = useCallback((values: Record<string, string>) => {
        latestValuesByNodeRef.current[nodeKeyRef.current] = values;
    }, []);

    const handleFileChange = useCallback((_key: string, file: File | null) => {
        selectedFileRef.current = file;
    }, []);

    const schemaFields = useMemo(() => {
        const raw = (sourceConfigurationData?.schema_definition as { source?: SchemaDefinitionField[] } | undefined)
            ?.source;
        return raw ?? EMPTY_SOURCE_SCHEMA;
    }, [sourceConfigurationData?.schema_definition]);

    const effectiveSchemaFields = useMemo(
        () => persistedNodeState?.schemaFields ?? schemaFields,
        [persistedNodeState, schemaFields],
    );
    const excelSchemaFields = useMemo(() => {
        if (!isExcelSource) return effectiveSchemaFields;
        const alreadyHasDelimiter = effectiveSchemaFields.some((field) => field.key === "file_delimiter");
        if (alreadyHasDelimiter) return effectiveSchemaFields;

        const maxOrder = effectiveSchemaFields.reduce((max, field) => (
            typeof field.order === "number" ? Math.max(max, field.order) : max
        ), 0);

        return [
            ...effectiveSchemaFields,
            {
                type: "matselect",
                key: "file_delimiter",
                label: "Delimiter",
                placeholder: "Select Delimiter",
                validators: ["required"],
                options: DELIMITERS.map((delimiter) => delimiter.name),
                show: true,
                order: maxOrder + 1,
            } satisfies SchemaDefinitionField,
        ];
    }, [effectiveSchemaFields, isExcelSource]);

    const isReadonlyConfigured = persistedNodeState?.isReadOnly === true;

    const schemaFieldsRef = useRef(schemaFields);
    schemaFieldsRef.current = schemaFields;

    const effectiveSchemaFieldsRef = useRef(excelSchemaFields);
    effectiveSchemaFieldsRef.current = excelSchemaFields;

    const postTestFieldsRef = useRef(postTestFields);
    postTestFieldsRef.current = postTestFields;

    const isPostTestSelectionComplete = useMemo(() => {
        if (isExcelSource) {
            return isBaseFormValid;
        }
        if (postTestFields.length === 0) return isConnectionTested;
        return isCombinedFormValid;
    }, [isExcelSource, isBaseFormValid, isCombinedFormValid, isConnectionTested, postTestFields.length]);

    const { mutate: runTestConnection, isPending: isTestConnectionPending } = useTestConnectionMutation({
        onSuccess: (response) => {
            const baseFields = effectiveSchemaFieldsRef.current;
            const maxSchemaOrder = baseFields.reduce((maxOrder, field) => {
                if (typeof field.order !== "number") return maxOrder;
                return Math.max(maxOrder, field.order);
            }, -1);
            const additionalFields = (response.testConnectionResponse ?? []).map((field, index) => ({
                ...field,
                order: maxSchemaOrder + index + 1,
            }));
            setNodeScopedState((prevState) => {
                const key = nodeKeyRef.current;
                const previousNodeState = prevState[key]
                    ?? buildEmptyNodeStateForPersisted(persistedNodeStateRef.current);

                return {
                    ...prevState,
                    [key]: {
                        ...previousNodeState,
                        postTestFields: additionalFields,
                        isConnectionTested: true,
                        isBaseFormValid: previousNodeState.isCombinedFormValid,
                        isCombinedFormValid: false,
                    },
                };
            });
        },
    });

    const { mutate: finalizeSourceConfiguration, isPending: isFinalizePending } = useFinalizeSourceConfigurationMutation();

    const handleNext = useCallback(() => {
        const src = sourceConfigurationDataRef.current;
        const persisted = persistedNodeStateRef.current;

        if (persisted?.isReadOnly === true) {
            dispatch(openOverlay({
                type: "explorer-dialog",
                data: {
                    nodeId: src?.nodeId ?? null,
                    data_source_type: src?.data_source_type ?? null,
                },
            }));
            return;
        }

        const values = latestValuesByNodeRef.current[nodeKeyRef.current] ?? persisted?.values ?? {};
        const sourceName = (values.source_name ?? persisted?.values?.source_name ?? "").trim();
        const resolvedDelimiter = DELIMITER_NAME_TO_VALUE.get(values.file_delimiter) ?? values.file_delimiter;

        finalizeSourceConfiguration({
            nodeId: src?.nodeId,
            sourceInstance: src?.data_source_instance,
            dataSourceType: src?.data_source_type ?? null,
            sourceName,
            values,
            schemaFields: effectiveSchemaFieldsRef.current,
            postTestFields: postTestFieldsRef.current,
            ...(isExcelSourceRef.current ? {
                file: selectedFileRef.current,
                fileDelimiter: resolvedDelimiter,
            } : {}),
        });
    }, [dispatch, finalizeSourceConfiguration]);

    const handleTestConnection = useCallback((values: Record<string, string>) => {
        const dynamicConfig = schemaFieldsRef.current.reduce<Record<string, string | null>>((acc, field) => {
            const key = field.key;
            if (!key) return acc;

            const currentValue = (values[key] ?? "").trim();
            acc[key] = currentValue.length > 0 ? currentValue : null;

            return acc;
        }, {});

        runTestConnection({
            databases: [
                {
                    type: sourceConfigurationDataRef.current?.data_source_instance ?? "",
                    ...dynamicConfig,
                },
            ],
        });
    }, [runTestConnection]);

    const combinedFields = useMemo(
        () => [...excelSchemaFields, ...postTestFields],
        [excelSchemaFields, postTestFields],
    );

    useEffect(() => {
        if (postTestFields.length === 0) return;

        const el = formContainerRef.current;
        if (!el) return;

        requestAnimationFrame(() => {
            el.scrollTo({
                top: el.scrollHeight,
                behavior: "smooth",
            });
        });
    }, [postTestFields]);

    let primaryFooterLabel = isExcelSource ? "Upload" : "Next";
    if (isFinalizePending) primaryFooterLabel = "Please wait...";

    return (
        <Sheet open={isSourceConfigurationOpen} onOpenChange={handleOpenChange}>
            <SheetContent side="right" noBackdrop topOffset={64} className="shadow-none">
                <SheetHeader>
                    <SheetTitle className="text-[18px] font-medium">Source Configuration</SheetTitle>
                </SheetHeader>
                {sourceConfigurationData && (
                    <>
                        <div ref={formContainerRef} className={`flex h-full flex-col gap-4 overflow-y-auto px-3 ${styles.thinScroll}`}>
                            <PickerDetailsHeader
                                type="source"
                                imageLink={sourceConfigurationData?.image_link}
                                sourceName={sourceConfigurationData?.data_source_name}
                                label="Source Name"
                                prerequisite={sourceConfigurationData?.prerequisite}
                            />
                            <div className="flex items-center gap-2 pb-2">
                                <Button variant="ghost" size="icon" onClick={handleBack}>
                                    <ChevronLeft />
                                </Button>
                                <h3 className="text-lg font-medium">Configuration Details</h3>
                            </div>
                            <SchemaDefinitionForm
                                type="source"
                                nodeId={sourceConfigurationData?.nodeId}
                                dbName={sourceConfigurationData?.data_source_name}
                                fields={combinedFields}
                                initialValues={persistedNodeState?.values}
                                onSubmit={handleTestConnection}
                                onValuesChange={handleValuesChange}
                                onValidityChange={handleValidityChange}
                                readOnly={isReadonlyConfigured}
                                submitDisabled={!isBaseFormValid || isTestConnectionPending || isConnectionTested}
                                showSubmitButton={!isExcelSource && !isConnectionTested && !isReadonlyConfigured}
                                submitLabel={isTestConnectionPending ? "Testing..." : "Test Connection"}
                                fileAccept={isExcelSource ? EXCEL_ACCEPT_STRING : undefined}
                                onFileChange={isExcelSource ? handleFileChange : undefined}
                            />
                            {isReadonlyConfigured && (
                                <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                                    Source configuration cannot be changed once configured.
                                </div>
                            )}
                        </div>
                        <SheetFooter className="grid grid-cols-2 gap-2 px-2 py-3 border-t">
                            <Button variant="outline" className="w-full" onClick={handleCancel}>Cancel</Button>
                            <Button
                                className="w-full"
                                disabled={!isReadonlyConfigured && (!isPostTestSelectionComplete || isFinalizePending)}
                                onClick={handleNext}
                            >
                                {primaryFooterLabel}
                            </Button>
                        </SheetFooter>
                    </>
                )}
            </SheetContent>
        </Sheet>
    );
};

export default SourceConfiguration;
